import createTheme from "./createTheme";
import type {ThemeOptions as MuiThemeOptions} from "@mui/material/styles";
import createOverrides from "./next/createOverrides";
import {createTypography} from "./next/typography";
import {createColor} from "./next/color";
import type {Color, ColorOptions} from "./next/color/types";

type BorderRadius = {
    none: number;
    subtlest: number;
    subtle: number;
    default: number;
    soft: number;
    full: number;
};

declare module "@mui/material/styles" {
    interface Theme {
        borderRadius: BorderRadius;
        color: Color;
    }

    interface ThemeOptions {
        borderRadius?: Partial<BorderRadius>;
        color?: ColorOptions;
    }
}

/**
 * custom `createTheme` wrapper to help generate options for Muis `createTheme` function
 *
 * **Note**: if you only need to access the current theme in a component, you should use the `useTheme` hook instead. Only use this function if you need to create a new theme object.
 */
export function createNextTheme(options: MuiThemeOptions = {}) {
    const {
        color: colorOptions = {},
        typography: typographyOptions,
        components: componentOptions,
        borderRadius: borderRadiusOptions,
        ...restOptions
    } = options;
    const color = createColor(colorOptions);
    const typography = createTypography(typographyOptions);
    const components = createOverrides(componentOptions);

    // eslint-disable-next-line atg/no-create-theme-in-function-call
    const theme = createTheme({
        // @ts-ignore
        typography,
        spacing: 4,
        borderRadius: {
            none: 0,
            subtlest: 2,
            subtle: 4,
            default: 8,
            soft: 16,
            full: 9999,
            ...borderRadiusOptions,
        },
        shape: {
            borderRadius: 8,
        },
        color,
        components,
        ...restOptions,
    });

    return theme;
}
